import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from '../environments/enviroment';
import Swal from 'sweetalert2';
import { Observable, catchError, map, of, BehaviorSubject, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { jwtDecode } from "jwt-decode";
declare let gtag: Function;

export interface PendingDeeplink {
  idContenido: string | null;
  idListaReproduccion: string | null;
  idClase: string | null;
  tipoContenido: string | null;
  listaReproduccion: string | null;
}

export interface UtmAttribution {
  session_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  referrer?: string;
  landing_path?: string;
  landing_url?: string;
  captured_at?: string;
}

export interface AuthSession {
  ok: true;
  token: string;
  user: { _id: string; nombre?: string; UserTypeArray?: string[] };
  contenidoEspecial?: string;
}

interface AuthResponse { data?: Partial<AuthSession> }

function parseAuthSession(response: AuthResponse | null): AuthSession {
  const data = response?.data;

  // String identity rejects JSON numbers, objects and arrays before their values are used.
  if (data?.ok !== true || !data.token || String(data.token) !== data.token || !data.token.trim() ||
      !data.user?._id || String(data.user._id) !== data.user._id || !data.user._id.trim()) {
    throw new Error('La respuesta de autenticación no es válida.');
  }

  return { ok: true, token: data.token, user: data.user, contenidoEspecial: data.contenidoEspecial };
}

@Injectable({
  providedIn: 'root'
})
export class CentralService {

  constructor( 
    private http: HttpClient,
    private router: Router,
    private location:Location) { }
    private baseUrl: string = environment.apiBaseUrl;
  
  private _usuario: any = {};

  private usuarioSubject = new BehaviorSubject<any>(null);
  private contenidoEspecialSubject = new BehaviorSubject<any>(this.getContenidoEspecialId());

  navegacionAreaTerapeutica: any = '';

  resetAreaTerapeutica(){
    this.navegacionAreaTerapeutica = '';
  }
  
  usuario$: Observable<any> = this.usuarioSubject.asObservable();

  contenidoEspecial$: Observable<any> = this.contenidoEspecialSubject.asObservable();

  private readonly deeplinkIdContenidoCookie = 'medclass_deeplink_idContenido';
  private readonly deeplinkIdListaReproduccionCookie = 'medclass_deeplink_idListaReproduccion';
  private readonly deeplinkIdClaseCookie = 'medclass_deeplink_idClase';
  private readonly deeplinkTipoContenidoCookie = 'medclass_deeplink_tipoContenido';
  private readonly deeplinkListaReproduccionCookie = 'medclass_deeplink_listaReproduccion';
  private readonly utmAttributionStorageKey = 'medclass_utm_attribution';
  private readonly utmLandingEventStorageKey = 'medclass_utm_landing_event_key';
  private readonly trackingSessionStorageKey = 'medclass_tracking_session_id';
  private readonly utmAttributionTtlMs = 7 * 24 * 60 * 60 * 1000;
  private readonly utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  private readonly clickIdKeys = ['gclid', 'fbclid', 'msclkid', 'ttclid'];
  get token(): boolean {
    return Boolean(localStorage.getItem('token'));
  }

  get usuario() {
    return { ...this._usuario  }
  }

  actualizaUsuarioLocal(usuarioParcial: any) {
    this._usuario = {
      ...this._usuario,
      ...usuarioParcial,
    };
    this.usuarioSubject.next(this.usuario);
  }

  extractRoleTokens(value: any): string[] {
    if (Array.isArray(value)) {
      return value.flatMap(item => this.extractRoleTokens(item));
    }

    if (value && typeof value === 'object') {
      return Object.values(value).flatMap(item => this.extractRoleTokens(item));
    }

    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);
        if (parsed !== value) {
          return this.extractRoleTokens(parsed);
        }
      } catch {
        // no-op
      }

      return raw.match(/[A-Za-zÁÉÍÓÚáéíóúÑñ_ -]+/g) || [];
    }

    return [];
  }

  normalizeRole(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_-]+/g, '')
      .trim()
      .toLowerCase();
  }

  getAllRoles(extraSources: any[] = []): string[] {
    const sources = [
      this.usuario?.UserTypeArray,
      localStorage.getItem('userTypeArray'),
      this.getTokenRoles(),
      ...extraSources
    ];

    const roles = sources
      .flatMap(source => this.extractRoleTokens(source))
      .map(role => this.normalizeRole(role))
      .filter(role => role.length > 0);

    return Array.from(new Set(roles));
  }

  hasAnyRole(roles: string[], extraSources: any[] = []): boolean {
    const normalizedRoles = roles.map(role => this.normalizeRole(role));
    const userRoles = this.getAllRoles(extraSources);

    return userRoles.some(role => normalizedRoles.includes(role));
  }

  isPuntoCimaUser(extraSources: any[] = []): boolean {
    return this.hasAnyRole(['Punto CIMA'], extraSources);
  }

  getContenidoEspecialId(): string {
    const contenidoEspecial = localStorage.getItem('contenidoEspecial');

    if (contenidoEspecial && contenidoEspecial !== 'undefined' && contenidoEspecial !== 'null') {
      return contenidoEspecial;
    }

    return environment.idContenidoEspecial || '';
  }

  private getTokenRoles(): string[] {
    const token = localStorage.getItem('token');
    if (!token) return [];

    try {
      const decoded: any = jwtDecode(token);

      return this.extractRoleTokens([
        decoded?.UserTypeArray,
        decoded?.role,
        decoded?.roles,
        decoded?.authorities,
        decoded?.usertype,
        decoded?.user_type,
        decoded?.userTypeArray,
        decoded?.user_type_array,
        decoded?.userTypes,
        decoded?.userType
      ]);
    } catch {
      return [];
    }
  }

  whiteList = ['ggg030@mail.com','ggg031@mail.com','lafernandez@silanes.com.mx'];
  puedeRegistrar(){
    // Membership administration belongs exclusively to an authenticated CMS administrator.
    return false;
  }

  Toast:any = Swal.mixin({
      toast: true,
      position: 'center',
      iconColor: '#FF0000',
      customClass: {
        popup: 'colored-toast',
      },
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    })

  checkContenidoEspecial(){
      const contenidoEspecial = this.getContenidoEspecialId();

      if (contenidoEspecial) {
        this.contenidoEspecialSubject.next(contenidoEspecial);
        // console.log('APLICA CONTENIDO');
      }else{
        console.log('NO TIENE CONTENIDO ESPECIAL');
      }
      
    }

  setDeeplinkCookies(
    idContenido?: string | null,
    idClase?: string | null,
    tipoContenido?: string | null,
    listaReproduccion?: boolean | string | null,
    idListaReproduccion?: string | null
  ) {
    if (idContenido) {
      this.setCookie(this.deeplinkIdContenidoCookie, idContenido);
    }

    if (idListaReproduccion) {
      this.setCookie(this.deeplinkIdListaReproduccionCookie, idListaReproduccion);
    } else if (listaReproduccion === false || listaReproduccion === 'false') {
      this.deleteCookie(this.deeplinkIdListaReproduccionCookie);
    }

    if (listaReproduccion !== undefined && listaReproduccion !== null) {
      this.setCookie(this.deeplinkListaReproduccionCookie, String(listaReproduccion));
    }

    if (idClase) {
      this.setCookie(this.deeplinkIdClaseCookie, idClase);
      this.setCookie(this.deeplinkTipoContenidoCookie, 'masterclass');
      return;
    }

    if (tipoContenido) {
      this.setCookie(this.deeplinkTipoContenidoCookie, tipoContenido);
    }
  }

  setDeeplinkCookiesFromParams(params: any, tipoContenido?: string | null, idListaReproduccion?: string | null) {
    const idContenido = params?.get ? params.get('idContenido') : params?.['idContenido'];
    const idClase = params?.get ? params.get('idClase') : params?.['idClase'];
    const listaReproduccion = params?.get ? params.get('listaReproduccion') : params?.['listaReproduccion'];
    this.setDeeplinkCookies(idContenido, idClase, tipoContenido, listaReproduccion, idListaReproduccion);
  }

  getPendingDeeplink(): PendingDeeplink {
    return {
      idContenido: this.getCookie(this.deeplinkIdContenidoCookie),
      idListaReproduccion: this.getCookie(this.deeplinkIdListaReproduccionCookie),
      idClase: this.getCookie(this.deeplinkIdClaseCookie),
      tipoContenido: this.getCookie(this.deeplinkTipoContenidoCookie),
      listaReproduccion: this.getCookie(this.deeplinkListaReproduccionCookie)
    };
  }

  clearPendingDeeplink() {
    this.deleteCookie(this.deeplinkIdContenidoCookie);
    this.deleteCookie(this.deeplinkIdListaReproduccionCookie);
    this.deleteCookie(this.deeplinkIdClaseCookie);
    this.deleteCookie(this.deeplinkTipoContenidoCookie);
    this.deleteCookie(this.deeplinkListaReproduccionCookie);
  }

  buildPendingDeeplinkUrl(): string | null {
    const pendingDeeplink = this.getPendingDeeplink();

    if (!pendingDeeplink.idContenido) {
      return null;
    }

    const idContenido = encodeURIComponent(pendingDeeplink.idContenido);

    if (pendingDeeplink.idClase) {
      const idClase = encodeURIComponent(pendingDeeplink.idClase);
      return `/contenido/masterclass/${idContenido}?idContenido=${idContenido}&idClase=${idClase}`;
    }

    if (!pendingDeeplink.tipoContenido) {
      return null;
    }

    if (pendingDeeplink.listaReproduccion === 'true') {
      const idListaReproduccion = encodeURIComponent(
        pendingDeeplink.idListaReproduccion || pendingDeeplink.idContenido
      );
      return `/contenido/lista-de-reproduccion/${encodeURIComponent(pendingDeeplink.tipoContenido)}/${idListaReproduccion}?idContenido=${idContenido}`;
    }

    return `/contenido/${encodeURIComponent(pendingDeeplink.tipoContenido)}/${idContenido}?idContenido=${idContenido}`;
  }

  private setCookie(name: string, value: string) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=86400; SameSite=Lax${secure}`;
  }

  private getCookie(name: string): string | null {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`));

    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
  }

  private deleteCookie(name: string) {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  }

  captureUtmAttribution(params: any, extraParams: Record<string, any> = {}): UtmAttribution | null {
    const hasPublicToken = params?.has
      ? params.has('publicToken')
      : Object.prototype.hasOwnProperty.call(params || {}, 'publicToken');

    if (hasPublicToken || new URLSearchParams(window.location.search).has('publicToken')) return null;

    const attribution: UtmAttribution = {};

    this.utmKeys.forEach(key => {
      const value = params?.get ? params.get(key) : params?.[key];
      if (value) {
        attribution[key as keyof UtmAttribution] = value;
      }
    });

    this.clickIdKeys.forEach(key => {
      const value = params?.get ? params.get(key) : params?.[key];
      if (value) {
        attribution[key as keyof UtmAttribution] = value;
      }
    });

    if (!Object.keys(attribution).length) {
      return this.getStoredUtmAttribution();
    }

    attribution.session_id = this.getTrackingSessionId();
    attribution.referrer = this.sanitizeAttributionUrl(document.referrer);
    attribution.landing_path = this.location.path(false).split(/[?#]/, 1)[0];
    attribution.landing_url = this.sanitizeAttributionUrl(window.location.href);
    attribution.captured_at = new Date().toISOString();
    localStorage.setItem(this.utmAttributionStorageKey, JSON.stringify(attribution));

    const landingEventKey = JSON.stringify({
      path: attribution.landing_path,
      source: attribution.utm_source,
      medium: attribution.utm_medium,
      campaign: attribution.utm_campaign,
      term: attribution.utm_term,
      content: attribution.utm_content,
      gclid: attribution.gclid,
      fbclid: attribution.fbclid,
      msclkid: attribution.msclkid,
      ttclid: attribution.ttclid
    });

    if (sessionStorage.getItem(this.utmLandingEventStorageKey) !== landingEventKey) {
      sessionStorage.setItem(this.utmLandingEventStorageKey, landingEventKey);
      this.trackCampaignEvent('campaign_landing', extraParams);
    }

    return attribution;
  }

  getStoredUtmAttribution(): UtmAttribution | null {
    const rawAttribution = localStorage.getItem(this.utmAttributionStorageKey);

    if (!rawAttribution) {
      return null;
    }

    try {
      const attribution = JSON.parse(rawAttribution);
      const capturedAt = attribution?.captured_at ? Date.parse(attribution.captured_at) : NaN;

      if (!capturedAt || Date.now() - capturedAt > this.utmAttributionTtlMs) {
        localStorage.removeItem(this.utmAttributionStorageKey);
        return null;
      }

      attribution.landing_url = this.sanitizeAttributionUrl(attribution.landing_url);
      attribution.referrer = this.sanitizeAttributionUrl(attribution.referrer);
      attribution.landing_path = String(attribution.landing_path || '').split(/[?#]/, 1)[0];
      localStorage.setItem(this.utmAttributionStorageKey, JSON.stringify(attribution));

      return attribution;
    } catch {
      localStorage.removeItem(this.utmAttributionStorageKey);
      return null;
    }
  }

  private sanitizeAttributionUrl(value?: string): string {
    if (!value) return '';

    try {
      const url = new URL(value);
      url.search = '';
      url.hash = '';

      return url.href;
    } catch {
      return '';
    }
  }

  getTrackingSessionId(): string {
    const storedSessionId = sessionStorage.getItem(this.trackingSessionStorageKey);

    if (storedSessionId) {
      return storedSessionId;
    }

    const sessionId = this.createTrackingSessionId();
    sessionStorage.setItem(this.trackingSessionStorageKey, sessionId);
    return sessionId;
  }

  trackCampaignEvent(eventName: string, extraParams: Record<string, any> = {}) {
    const attribution = this.getStoredUtmAttribution();

    if (!attribution) {
      return;
    }

    this.event(eventName, {
      ...this.formatUtmEventParams(attribution),
      ...extraParams
    });
  }

  private formatUtmEventParams(attribution: UtmAttribution) {
    return {
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      source: attribution.utm_source,
      medium: attribution.utm_medium,
      campaign: attribution.utm_campaign,
      campaign_source: attribution.utm_source,
      campaign_medium: attribution.utm_medium,
      campaign_name: attribution.utm_campaign,
      campaign_term: attribution.utm_term,
      campaign_content: attribution.utm_content,
      gclid: attribution.gclid,
      fbclid: attribution.fbclid,
      msclkid: attribution.msclkid,
      ttclid: attribution.ttclid,
      referrer: attribution.referrer,
      session_id: attribution.session_id,
      landing_path: attribution.landing_path,
      landing_url: attribution.landing_url,
      captured_at: attribution.captured_at
    };
  }

  private createTrackingSessionId(): string {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  
  
    disparaToast(mensaje:string, error?: boolean){
      this.Toast.fire({
        icon: error ? 'error' : 'success',
        title: mensaje,
        position: 'bottom-end',
        customClass: "medclass_toaster",
        background: "#313131",
        color: "#fff",
        showClass: {
          popup: `fade_in`
        },
        hideClass: {
          popup: ``
        }
      })
    }

  actualizaCampoOnboarding(userId:string){
    const url = `${ this.baseUrl }/ocultarOnboarding`;
    const token = localStorage.getItem('token');
    const body = {id:userId};
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    })
    
    return this.http.post<any>( url, body, { headers } );
  }

  getNotificacion(){
    const url = `${ this.baseUrl }/notificaciones`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    })
    return this.http.get( url, { headers } );
  }  

  getUsuariosCarmen(){
    const url = `${ this.baseUrl }/recuperaUsuariosCarmen3`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    })
    return this.http.get( url, { headers } );
  }  

  toggleInscrito(obj:any){
    const url = `${ this.baseUrl }/actualizarUsuariosCarmen3`;
    const body = obj;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    })
    return this.http.post<any>( url, body, { headers } );
  }

  getWebinarActivo(){
    const url = `${ this.baseUrl }/webinarActivo`;
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    })
    return this.http.get( url, { headers } );
  }  
  
  logout(){
    const token = localStorage.getItem('token');
    // Verificar si el token existe antes de intentar eliminarlo
    if (token) {
      // Eliminar el token
      localStorage.removeItem('token');
      localStorage.removeItem('contenidoEspecial');
      localStorage.removeItem('userTypeArray');
      this.router.navigateByUrl('/inicio')
    } else {
      //console.log('No hay token almacenado para eliminar');
    }
  }

  public event(eventName: string, params: {}) {
    if (new URLSearchParams(window.location.search).has('publicToken')) return;

    if (typeof gtag !== 'function') {
      return;
    }

    gtag('event', eventName, {
      ...params,
      ...this.getDebugModeParam()
    });
  }

  public pageView() {
    if (new URLSearchParams(window.location.search).has('publicToken')) return;

    if (typeof gtag !== 'function') {
      return;
    }

    const pageLocation = new URL(window.location.href);

    pageLocation.search = '';
    pageLocation.hash = '';
    gtag('config', 'G-102EB3SJVC', {
      page_path: pageLocation.pathname,
      page_location: pageLocation.href,
      ...this.getDebugModeParam()
    });
  }

  private getDebugModeParam() {
    const hostname = window.location.hostname;

    return hostname === 'localhost' || hostname === '127.0.0.1'
      ? { debug_mode: true }
      : {};
  }

  login(tokenNMS: string): Observable<AuthSession> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/userToken`, { tokenNMS }).pipe(
      timeout(15000),
      map(parseAuthSession),
      map(session => {
        this.storeSession(session);

        if (session.contenidoEspecial) {
          localStorage.setItem('contenidoEspecial', session.contenidoEspecial);
        } else {
          localStorage.removeItem('contenidoEspecial');
        }

        this.contenidoEspecialSubject.next(this.getContenidoEspecialId());

        return session;
      })
    );
  }

  private storeSession(session: AuthSession): void {
    localStorage.setItem('token', session.token);
    localStorage.setItem('userTypeArray', JSON.stringify(session.user.UserTypeArray || []));
    this._usuario = session.user;
    this.usuarioSubject.next(session.user);
  }

  isLoggedIn(): Observable<boolean> {
    
    let authToken = localStorage.getItem('token');
    const url = `${this.baseUrl}/auth/renewToken`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    })

    return this.http.get<AuthResponse>(url, { headers })
      .pipe(
        timeout(15000),
        map(parseAuthSession),
        map(session => {
          this.storeSession(session);
          this.contenidoEspecialSubject.next(this.getContenidoEspecialId());

          return true;
        }),
        catchError(() => of(false))
      )
  }

  validarToken() {
    const url = `${ this.baseUrl }/auth/renew`;
    const headers = new HttpHeaders()
      .set('x-token', localStorage.getItem('token') || '' );

    return this.http.get( url, { headers } );
  }

  tieneToken(): boolean {
    const token = localStorage.getItem('token');
    return token !== null && token !== undefined;
  }
}
